import {
  ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES,
  IssueActionProgressEnum,
  IssueApprovalStatusEnum,
  IssueClassificationEnum,
  IssueResolutionEnum,
  IssueReviewStatusEnum,
  IssueSeverityEnum,
  IssueStatusEnum,
  IssueTypeEnum,
  IssueTypeValues,
  SLAStatusEnum,
  SourceAppEnum,
  SourceEntityTypeValues,
} from '@issuemanager/constants';
import { z } from 'zod';
import { actionQueryResponseSchema } from '../action/action.schema';
import { attachmentQueryResponseSchema } from '../attachment';
import { govmapGroupWithParentNameSchema } from '../govmap-group';
import { minimalIssueSubtypeSchema } from '../issue-subtype';
import { minimalIssueTypeSchema } from '../issue-type';
import { minimalOrganizationSchema } from '../organization';
import { minimalUserSchema, watcherUserSchema } from '../user';
import { minimalIssueTimelineSchema } from './issue-timeline.schema';

// 1-to-1 mapping with database columns
const issueTableSchema = z.object({
  id: z.string(),
  created_at: z.number().transform((date) => new Date(date)),
  updated_at: z.number().transform((date) => new Date(date)),
  description: z.string().nullable(),
  impact: z.string().nullable(),
  severity: z.nativeEnum(IssueSeverityEnum).nullable(),
  user_id: z.string(),
  issue_subtype_id: z.string().nullable(),
  owner_id: z.string().uuid().nullable(),
  approver_id: z.string().uuid().nullable(),
  due_date: z
    .number()
    .transform((value) => new Date(value))
    .nullable(),
  actual_remediation_date: z
    .number()
    .transform((value) => new Date(value))
    .nullable(),
  code: z.string(),
  status: z.nativeEnum(IssueStatusEnum),
  selected_template: z.string().nullable(),
  source_entity_type: z.enum(SourceEntityTypeValues).nullable(),
  source_entity_id: z.string().nullable(),
  source_entity_name: z.string().nullable(),
  source_entity_link: z.string().nullable(),
  source_application: z.nativeEnum(SourceAppEnum),
  organization_id: z.string(),
  action_progress: z.nativeEnum(IssueActionProgressEnum).nullable(),
  approval_status: z.nativeEnum(IssueApprovalStatusEnum),
  execution_type: z.enum(ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES),
  is_workflow_blocked: z.boolean(),
  issue_type: z.enum(IssueTypeValues),
  title: z.string(),
  // Shared fields for Audit Finding & Control Deficiency
  classification: z.nativeEnum(IssueClassificationEnum).nullable(),
  root_cause: z.string().nullable(),
  management_response: z.string().nullable(),
  resolution: z.nativeEnum(IssueResolutionEnum).nullable(),
  // Audit Finding specific fields
  include_in_audit_report: z.boolean().nullable(),
  recommendations: z.string().nullable(),
  review_status: z.nativeEnum(IssueReviewStatusEnum).nullable(),
  date_published: z
    .number()
    .transform((value) => new Date(value))
    .nullable(),
  // Control Deficiency specific fields
  remediation: z.string().nullable(),
  // SLA state columns
  sla_enabled: z.boolean(),
  sla_state_reason: z.string().nullable(),
  sla_state_changed_by: z.string().nullable(),
  sla_state_changed_at: z
    .union([z.number(), z.string()])
    .transform((n) => new Date(n))
    .nullable(),
});

type IssueTableSchemaType = z.infer<typeof issueTableSchema>;

// Common transformer for converting Issue columns from snake_case to camelCase
const issueSchemaTransformer = (issue: IssueTableSchemaType) => ({
  id: issue.id,
  createdAt: issue.created_at,
  updatedAt: issue.updated_at,
  description: issue.description,
  impact: issue.impact,
  severity: issue.severity,
  userId: issue.user_id,
  issueSubtypeId: issue.issue_subtype_id,
  ownerId: issue.owner_id,
  approverId: issue.approver_id,
  dueDate: issue.due_date,
  actualRemediationDate: issue.actual_remediation_date,
  code: issue.code,
  status: issue.status,
  selectedTemplate: issue.selected_template,
  executionType: issue.execution_type,
  source: issue.source_application
    ? {
        entityType: issue.source_entity_type ?? null,
        id: issue.source_entity_id ?? null,
        name: issue.source_entity_name ?? null,
        link: issue.source_entity_link ?? null,
        app: issue.source_application,
      }
    : null,
  organizationId: issue.organization_id,
  // TODO: action_progress is in the table but not needed (always calculated).
  // Consider creating a migration to remove the field
  // TASK: https://diligentbrands.atlassian.net/browse/RCP-20544
  actionProgress: issue.action_progress,
  approvalStatus: issue.approval_status,
  isWorkflowBlocked: issue.is_workflow_blocked,
  issueType: issue.issue_type,
  title: issue.title,
  // Shared fields for Audit Finding & Control Deficiency
  classification: issue.classification,
  rootCause: issue.root_cause,
  managementResponse: issue.management_response,
  resolution: issue.resolution,
  // Audit Finding specific fields
  includeInAuditReport: issue.include_in_audit_report,
  recommendations: issue.recommendations,
  reviewStatus: issue.review_status,
  datePublished: issue.date_published,
  // Control Deficiency specific fields
  remediation: issue.remediation,
  // SLA state
  slaEnabled: issue.sla_enabled,
  slaStateReason: issue.sla_state_reason,
  slaStateChangedBy: issue.sla_state_changed_by,
  slaStateChangedAt: issue.sla_state_changed_at,
});

export const issueSchema = issueTableSchema.transform(issueSchemaTransformer);

export type IssueSchemaType = z.infer<typeof issueSchema>;

export const issueQueryResponseSchema = z
  .object({
    id: z.string(),
    description: z.string().nullable(),
    impact: z.string().nullable(),
    code: z.string(),
    severity: z.nativeEnum(IssueSeverityEnum).nullable(),
    execution_type: z.enum(ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES),
    owner_id: z.string().nullable(),
    created_at: z.number().transform((date) => new Date(date)),
    updated_at: z.number().transform((date) => new Date(date)),
    user_id: z.string(),
    approver_id: z.string().nullable(),
    due_date: z
      .number()
      .transform((date) => new Date(date))
      .nullable(),
    actual_remediation_date: z
      .number()
      .transform((date) => new Date(date))
      .nullable(),
    issue_subtype_id: z.string().nullable(),
    source_entity_type: z.enum(SourceEntityTypeValues).nullable(),
    source_entity_id: z.string().nullable(),
    source_entity_name: z.string().nullable(),
    source_entity_link: z.string().nullable(),
    source_application: z.nativeEnum(SourceAppEnum),
    selected_template: z.string().nullable(),
    sla_days: z.number().nullable(),
    max_due_date: z.number().int().gte(0).nullable(),
    escalation_days: z.number().nullable(),
    reminder_days: z.array(z.number().int().gte(0)).nullable(),
    sla_due_days: z.number().nullable(),
    creator: minimalUserSchema,
    owner: minimalUserSchema.nullable(),
    approver: minimalUserSchema.nullable(),
    issue_subtype: minimalIssueSubtypeSchema.nullable(),
    actions: z.array(actionQueryResponseSchema),
    actionDrafts: z.array(actionQueryResponseSchema).nullable(),
    managers: z.array(minimalUserSchema).nullable(),
    watchers: z.array(watcherUserSchema).nullable(),
    govmap_groups: z.array(govmapGroupWithParentNameSchema),
    timeline_events: z.array(minimalIssueTimelineSchema).nullable(),
    sla_enabled: z.boolean(),
    sla_state_reason: z.string().nullable(),
    sla_state_changed_by: z.string().nullable(),
    sla_state_changed_by_name: z.string().nullable(),
    sla_state_changed_at: z
      .union([z.number(), z.string()])
      .transform((n) => new Date(n))
      .nullable(),
    sla_status: z.nativeEnum(SLAStatusEnum).nullable(),
    organization_id: z.string(),
    status: z.nativeEnum(IssueStatusEnum),
    organization: minimalOrganizationSchema,
    is_issue_type_manager: z.boolean(),
    is_action_owner: z.boolean(),
    is_action_approver: z.boolean(),
    action_count: z.number().nullable().optional(),
    relationship_count: z.number().nullable().optional(),
    matching_source_entities: z
      .array(
        z.object({
          sourceEntityType: z.string(),
          sourceEntityId: z.string(),
          sourceEntityService: z.string(),
        }),
      )
      .nullable()
      .optional(),
    action_progress: z.nativeEnum(IssueActionProgressEnum).nullable(),
    approval_status: z.nativeEnum(IssueApprovalStatusEnum),
    attachments: z.array(attachmentQueryResponseSchema).optional(),
    is_workflow_blocked: z.boolean(),
    issue_type: minimalIssueTypeSchema,
    title: z.string(),
    // Shared fields for Audit Finding & Control Deficiency
    classification: z.nativeEnum(IssueClassificationEnum).nullable(),
    root_cause: z.string().nullable(),
    management_response: z.string().nullable(),
    resolution: z.nativeEnum(IssueResolutionEnum).nullable(),
    // Audit Finding specific fields
    include_in_audit_report: z.boolean().nullable(),
    recommendations: z.string().nullable(),
    review_status: z.nativeEnum(IssueReviewStatusEnum).nullable(),
    date_published: z
      .number()
      .transform((date) => new Date(date))
      .nullable(),
    // Control Deficiency specific fields
    remediation: z.string().nullable(),
  })
  .transform((issue) => ({
    ...issue,
    organizationId: issue.organization_id,
    actionProgress: issue.action_progress,
    relationshipCount: issue.relationship_count ?? 0,
    matchingSourceEntities: issue.matching_source_entities ?? null,
    govmapGroups: issue.govmap_groups,
    timelineEvents: issue.timeline_events,
    slaStatus: issue.sla_status,
    slaDueDays: issue.sla_due_days,
    slaEnabled: issue.sla_enabled,
    slaStateReason: issue.sla_state_reason,
    slaStateChangedBy: issue.sla_state_changed_by,
    slaStateChangedByName: issue.sla_state_changed_by_name,
    slaStateChangedAt: issue.sla_state_changed_at,
    reminderDays: issue.reminder_days,
    escalationDays: issue.escalation_days,
    maxDueDate: issue.max_due_date,
    slaDays: issue.sla_days,
    approvalStatus: issue.approval_status,
    selectedTemplate: issue.selected_template,
    executionType: issue.execution_type,
    dueDate: issue.due_date,
    actualRemediationDate: issue.actual_remediation_date,
    updatedAt: issue.updated_at,
    createdAt: issue.created_at,
    source: issue.source_application
      ? {
          entityType: issue.source_entity_type ?? null,
          id: issue.source_entity_id ?? null,
          name: issue.source_entity_name ?? null,
          link: issue.source_entity_link ?? null,
          app: issue.source_application,
        }
      : null,
    attachments: issue.attachments,
    isWorkflowBlocked: issue.is_workflow_blocked,
    issueType: issue.issue_type,
    issueSubtype: issue.issue_subtype,
    isIssueTypeManager: issue.is_issue_type_manager,
    // Shared fields for Audit Finding & Control Deficiency
    classification: issue.classification,
    rootCause: issue.root_cause,
    managementResponse: issue.management_response,
    resolution: issue.resolution,
    // Audit Finding specific fields
    includeInAuditReport: issue.include_in_audit_report,
    recommendations: issue.recommendations,
    reviewStatus: issue.review_status,
    datePublished: issue.date_published,
    // Control Deficiency specific fields
    remediation: issue.remediation,
  }));

export type IssueQueryResponseSchemaType = z.infer<
  typeof issueQueryResponseSchema
>;

const baseIssueSchema = issueTableSchema.omit({
  classification: true,
  root_cause: true,
  management_response: true,
  resolution: true,
  include_in_audit_report: true,
  recommendations: true,
  review_status: true,
  date_published: true,
  remediation: true,
});

const standardIssueDatabaseSchema = baseIssueSchema.extend({
  issue_type: z.enum([
    IssueTypeEnum.ISSUE,
    IssueTypeEnum.INCIDENT,
    IssueTypeEnum.EXCEPTION,
  ]),
  classification: z.null(),
  root_cause: z.null(),
  management_response: z.null(),
  resolution: z.null(),
  include_in_audit_report: z.null(),
  recommendations: z.null(),
  review_status: z.null(),
  date_published: z.null(),
  remediation: z.null(),
});

const auditFindingDatabaseSchema = baseIssueSchema.extend({
  issue_type: z.literal(IssueTypeEnum.AUDIT_FINDING),
  root_cause: z.string().nullable(),
  management_response: z.string().nullable(),
  include_in_audit_report: z.boolean().nullable(),
  recommendations: z.string().nullable(),
  classification: z.nativeEnum(IssueClassificationEnum).nullable(),
  resolution: z.nativeEnum(IssueResolutionEnum).nullable(),
  review_status: z.nativeEnum(IssueReviewStatusEnum).nullable(),
  date_published: z
    .number()
    .transform((value) => new Date(value))
    .nullable(),
  remediation: z.null(),
});

const controlDeficiencyDatabaseSchema = baseIssueSchema.extend({
  issue_type: z.literal(IssueTypeEnum.CONTROL_DEFICIENCY),
  root_cause: z.string().nullable(),
  management_response: z.string().nullable(),
  include_in_audit_report: z.null(),
  recommendations: z.null(),
  classification: z.nativeEnum(IssueClassificationEnum).nullable(),
  resolution: z.nativeEnum(IssueResolutionEnum).nullable(),
  review_status: z.null(),
  date_published: z.null(),
  remediation: z.string().nullable(),
});

export const issueDatabaseSchemaByType = z.discriminatedUnion('issue_type', [
  standardIssueDatabaseSchema,
  auditFindingDatabaseSchema,
  controlDeficiencyDatabaseSchema,
]);

const standardIssueInputSchema = z.object({
  issueType: z.enum([
    IssueTypeEnum.ISSUE,
    IssueTypeEnum.INCIDENT,
    IssueTypeEnum.EXCEPTION,
  ]),
  classification: z.null().optional(),
  rootCause: z.null().optional(),
  managementResponse: z.null().optional(),
  resolution: z.null().optional(),
  recommendations: z.null().optional(),
  includeInAuditReport: z.null().optional(),
  reviewStatus: z.null().optional(),
  datePublished: z.null().optional(),
  remediation: z.null().optional(),
});

const auditFindingInputSchema = z.object({
  issueType: z.literal(IssueTypeEnum.AUDIT_FINDING),
  classification: z.nativeEnum(IssueClassificationEnum).nullable().optional(),
  rootCause: z.string().nullable().optional(),
  managementResponse: z.string().nullable().optional(),
  resolution: z.nativeEnum(IssueResolutionEnum).nullable().optional(),
  recommendations: z.string().nullable().optional(),
  includeInAuditReport: z.boolean().nullable().optional(),
  reviewStatus: z.nativeEnum(IssueReviewStatusEnum).nullable().optional(),
  datePublished: z.date().nullable().optional(),
  remediation: z.null().optional(),
});

const controlDeficiencyInputSchema = z.object({
  issueType: z.literal(IssueTypeEnum.CONTROL_DEFICIENCY),
  classification: z.nativeEnum(IssueClassificationEnum).nullable().optional(),
  rootCause: z.string().nullable().optional(),
  managementResponse: z.string().nullable().optional(),
  resolution: z.nativeEnum(IssueResolutionEnum).nullable().optional(),
  remediation: z.string().nullable().optional(),
  recommendations: z.null().optional(),
  includeInAuditReport: z.null().optional(),
  reviewStatus: z.null().optional(),
  datePublished: z.null().optional(),
});

export const issueInputSchemaByType = z.discriminatedUnion('issueType', [
  standardIssueInputSchema,
  auditFindingInputSchema,
  controlDeficiencyInputSchema,
]);

const baseSubmitSchema = z.object({
  description: z.string().min(1, 'description is required'),
  severity: z.nativeEnum(IssueSeverityEnum),
});

const standardIssueSubmitSchema = baseSubmitSchema.extend({
  issueType: z.enum([
    IssueTypeEnum.ISSUE,
    IssueTypeEnum.INCIDENT,
    IssueTypeEnum.EXCEPTION,
  ]),
});

function getAuditFindingSubmitSchema(isIssueTypesExtraFieldsEnabled: boolean) {
  if (isIssueTypesExtraFieldsEnabled) {
    return baseSubmitSchema.extend({
      issueType: z.literal(IssueTypeEnum.AUDIT_FINDING),
      classification: z.nativeEnum(IssueClassificationEnum),
      rootCause: z.string().min(1, 'rootCause is required'),
      recommendations: z.string().min(1, 'recommendations is required'),
    });
  }
  return baseSubmitSchema.extend({
    issueType: z.literal(IssueTypeEnum.AUDIT_FINDING),
  });
}

function getControlDeficiencySubmitSchema(
  isIssueTypesExtraFieldsEnabled: boolean,
) {
  if (isIssueTypesExtraFieldsEnabled) {
    return baseSubmitSchema.extend({
      issueType: z.literal(IssueTypeEnum.CONTROL_DEFICIENCY),
      classification: z.nativeEnum(IssueClassificationEnum),
      rootCause: z.string().min(1, 'rootCause is required'),
    });
  }
  return baseSubmitSchema.extend({
    issueType: z.literal(IssueTypeEnum.CONTROL_DEFICIENCY),
  });
}

export function getIssueSubmitSchemaByType(
  isIssueTypesExtraFieldsEnabled: boolean,
) {
  return z.discriminatedUnion('issueType', [
    standardIssueSubmitSchema,
    getAuditFindingSubmitSchema(isIssueTypesExtraFieldsEnabled),
    getControlDeficiencySubmitSchema(isIssueTypesExtraFieldsEnabled),
  ]);
}

export const issueSubmitSchemaByType = getIssueSubmitSchemaByType(false);
